import { useState, useContext, useRef } from "react"
import intl from "react-intl-universal"
import { useLocation } from "react-router-dom"

import styles from "./index.module.scss"
import useClickOutside from "../../../hooks/useClickOutside"
import CircleGraphic from "../../atoms/CircleGraphic/index"
import DashboardIcon from "../../../assets/icons/DashboardIcon"
import RightArrow from "../../../assets/icons/right-arrow.svg"
import HomeIcon from "../../../assets/icons/HomeIcon"
import MedalIcon from "../../../assets/icons/MedalIcon"
import CheckedIcon from "../../../assets/icons/CheckedIcon"
import ProfileIcon from "../../../assets/icons/ProfileIcon"
import AuthContext from "../../../contexts/Auth"
import SignalIcon from "../../../assets/icons/SignalIcon"
import DocumentIcon from "../../../assets/icons/DocumentIcon"
import RcmpLink from "../../atoms/Link"
import { fnCanAtLeast } from "../../../utils/permissions"
import UserIcon from "../../../assets/icons/UserIcon"
import TeamIcon from "../../../assets/icons/TeamIcon"
import TrashIcon from "../../../assets/icons/TrashIcon"
import Button from "../../atoms/Button"
import SideBarContext from "../../../contexts/SideBar"
import FileIcon from "../../../assets/icons/FileIco"
import CIQIcon from "../../../assets/icons/CIQIcon"
import { BrandImage } from "../../../contexts/Appearance"

function SubMenu(props) {
  const { items, bSideBarIsOpen, className, ...oRest } = props

  if (!items || !bSideBarIsOpen) return null

  return (
    <div className={`${styles.subMenu} ${className || ""}`} {...oRest}>
      {items.map(({ path, name, ...oRest }, index) => {
        if (path) {
          return (
            <RcmpLink
              key={index}
              to={path}
              exact
              className={styles.subMenuItem}
              activeClassName={styles.subMenuItemActive}
            >
              {name}
            </RcmpLink>
          )
        }

        return (
          <div key={index} className={styles.subMenuItem} {...oRest}>
            {name}
          </div>
        )
      })}
    </div>
  )
}

function SideBarItemContent(props) {
  const { item, bShowTexts, bSubItemsOpen } = props
  return (
    <>
      <div className={styles.icon}>{item.icon}</div>
      <div className={bShowTexts ? `${styles.linkText}` : `${styles.hiddenText}`}>
        <span>{item.name}</span>
      </div>
      <SubMenu bSideBarIsOpen={bSubItemsOpen} items={item?.subItems} />
    </>
  )
}

function SideBarItem(props) {
  const { item, bShowTexts } = props
  const menuActionExceptions = ['folder']
  const [bSubItemsOpen, fnSetSubBitemsOpen] = useState(false)

  const subMenuRef = useRef(null)
  const location = useLocation()

  useClickOutside(subMenuRef, () => fnSetSubBitemsOpen(false))

  if (!item?.bShow) return null

  const hasSubItems = !!item?.subItems?.length

  const handleOnClick = () => {
    fnSetSubBitemsOpen(!bSubItemsOpen)
  }

  if (hasSubItems) {
    return (
      <div
        className={`${styles.link} ${styles.subMenuParent}`}
        onClick={handleOnClick}
        ref={subMenuRef}
      >
        <SideBarItemContent
          item={item}
          bShowTexts={bShowTexts}
          bSubItemsOpen={bSubItemsOpen}
        />
      </div>
    )
  }

  const end = item.path === "/" ? true : false

  let cActiveClass
  if (location.pathname === "/") {
    cActiveClass = item.path === "/" ? styles.activeLink : ""
  } else {
    cActiveClass =
      (location.pathname.replace("/", "") === item.path.replace("/", "") || (location.pathname.includes(menuActionExceptions)) && item.path.includes(menuActionExceptions))
        ? styles.activeLink
        : ""
  }

  return (
    <RcmpLink
      to={item.path}
      end={end}
      exact
      className={`${styles.link} ${cActiveClass}`}
      activeClassName={styles.activeLink}
    >
      <SideBarItemContent item={item} bShowTexts={bShowTexts} />
    </RcmpLink>
  )
}

function ProfileButton(props) {
  const { bShowTexts } = props

  const { oUser, fnLogout } = useContext(AuthContext)

  const [bSubItemsOpen, fnSetSubBitemsOpen] = useState(false)

  const subMenuRef = useRef(null)

  useClickOutside(subMenuRef, () => fnSetSubBitemsOpen(false))

  const cUserName = oUser?.data?.user_cname || ""
  const cUserAvatar = oUser?.data?.user_cavatar

  const handleOnClick = () => {
    fnSetSubBitemsOpen(!bSubItemsOpen)
  }

  const items = [
    {
      path: "./my-account/my-profile",
      name: intl.get("sidemenu.my-profile"),
    },
    {
      path: "./quality-panel-consultant",
      name: intl.get("sidemenu.quality-panel-consultant"),
    },
    {
      name: intl.get("sidemenu.logout"),
      onClick: fnLogout,
    },
  ]

  return (
    <div className={styles.profileButton} onClick={handleOnClick} ref={subMenuRef}>
      <div className={`${styles.link} ${styles.profileLink}`}>
        <div className={styles.icon}>
          {cUserAvatar?.length ? <img src={cUserAvatar} /> : <ProfileIcon />}
        </div>
        <div className={bShowTexts ? `${styles.linkText}` : `${styles.hiddenText}`}>
          <span>
            {cUserName.length > 17 ? `${cUserName.slice(0, 17)}...` : cUserName}
          </span>
        </div>
      </div>
      <SubMenu
        bSideBarIsOpen={bSubItemsOpen}
        items={items}
        className={styles.profileSubMenu}
      />
    </div>
  )
}

export default function SideBar({ children }) {
  const {
    bSideBarIsOpen,
    fnSetBSideBarIsOpen,
    bSideBarOnAfterOpen,
    fnSetBSideBarOnAfterOpen,
  } = useContext(SideBarContext)
  const { oUser } = useContext(AuthContext)
  const location = useLocation()

  const handleState = () => {
    fnSetBSideBarIsOpen(!bSideBarIsOpen)
  }

  const fnOnAfterOpen = () => {
    fnSetBSideBarOnAfterOpen(bSideBarIsOpen)
  }

  const menuItem = [
    {
      path: "./",
      name: intl.get("home"),
      icon: <HomeIcon />,
      bShow: true,
    },
    {
      path: "./quality-panel",
      name: intl.get("sidemenu.quality-panel"),
      icon: <MedalIcon />,
      bShow: fnCanAtLeast("quality-panel", "quality-panel"),
    },
    {
      path: "./audit-management",
      name: intl.get("sidemenu.audit-management"),
      icon: <CheckedIcon />,
      bShow: fnCanAtLeast("audit-management", "audit-management"),
    },
    {
      path: "/",
      name: intl.get("sidemenu.dashboard"),
      icon: <DashboardIcon />,
      bShow: fnCanAtLeast('operational-panel', 'operational-panel')
    },
    {
      path: "./folder",
      name: intl.get("sidemenu.folder"),
      icon: <DocumentIcon />,
      bShow: fnCanAtLeast("documents", "documents"),
    },
    {
      path: "/ciq",
      name: "CIQ",
      icon: <CIQIcon />,
      bShow: fnCanAtLeast('access-ciq', 'internal-quality-control'),
    },
    {
      path: "/reports",
      name: intl.get("sidemenu.reports"),
      icon: <SignalIcon />,
      bShow: fnCanAtLeast(["documents", "document-reading"], "documents"),
      subItems: [
        {
          path: "./report/reading-document",
          name: intl.get("sidemenu.reading-document"),
          bShow: fnCanAtLeast("document-reading", "documents"),
        },
        {
          path: "./report/reading-document-delegate",
          name: intl.get("sidemenu.reading-document-delegate"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/reading-document-responsible",
          name: intl.get("sidemenu.reading-document-responsible"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/edits-document-delegate",
          name: intl.get("sidemenu.edits-document-delegate"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/edits-document-responsible",
          name: intl.get("sidemenu.edits-document-responsible"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/request",
          name: intl.get("sidemenu.request"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/pending-per-user",
          name: intl.get("sidemenu.pending-per-user"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/activities-pending",
          name: intl.get("sidemenu.pending-activities"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
        {
          path: "./report/master-list",
          name: intl.get("sidemenu.master-list"),
          bShow: fnCanAtLeast("documents", "documents"),
        },
      ],
    },
  ]

  const menuPersonal = [
    {
      path: "./user",
      name: intl.get("sidemenu.users"),
      icon: <UserIcon />,
      bShow: fnCanAtLeast(["create-edit-user", "admin"]),
    },
    {
      path: "./team",
      name: intl.get("sidemenu.team"),
      icon: <TeamIcon />,
      bShow: fnCanAtLeast(["create-edit-team", "admin"]),
    },
    {
      path: "./trash",
      name: intl.get("sidemenu.trash"),
      icon: <TrashIcon />,
      bShow: fnCanAtLeast("admin"),
    },
  ]

  const cUserName = oUser?.data?.user_cname || ""
  const cUserAvatar = oUser?.data?.user_cavatar

  const nSideBarWidth = bSideBarIsOpen ? 240 : 96
  const bShowTexts = bSideBarIsOpen && bSideBarOnAfterOpen

  const oCustomer = oUser.data?.customer
  const nDiskSpace = oCustomer?.nDiskSpace
  let nQuotausedRaw = oCustomer?.cust_nused_space_quota_mb || 0

  if (!nQuotausedRaw) {
    nQuotausedRaw = Number(nQuotausedRaw);
    nQuotausedRaw /= 1024 * 1024 * 1024;
  } else {
    nQuotausedRaw /= 1000;
  }
  const nQuotaused = nQuotausedRaw?.toFixed(2)
  const nStoragePercentage = (nQuotaused * 100) / nDiskSpace || 0

  return (
    <div>
      <div
        className={`${styles.sidebarMask}  ${bSideBarIsOpen ? `${styles.isOpen}` : ""}`}
        onClick={handleState}
      ></div>
      <div className={styles.sidebarContainer}>


        <div
          style={{ minWidth: nSideBarWidth }}
          className={`${styles.sidebarAnchor}  ${bSideBarIsOpen ? `${styles.isOpenAnchor}` : ""}`}
          onTransitionEnd={fnOnAfterOpen}
        >
          <div className={`${styles.sidebar}  ${bSideBarIsOpen ? `${styles.isOpen}` : ""}`}>
            <div className={styles.topSection}>
              <RcmpLink
                className={
                  bSideBarIsOpen ? `${styles.displayInlineBlock}` : `${styles.displayNone}`
                }
                to="./"
              >
                <BrandImage
                  cFileName={bSideBarIsOpen ? "logo-text-112x61.svg" : "logo-56x62.svg"}
                  className={styles.desktopLogo}
                  bHasTitleAlt
                />
                <BrandImage
                  cFileName="logo-text-inline-140x24.svg"
                  className={styles.mobileLogo}
                  bHasTitleAlt
                />
              </RcmpLink>
              <div className={styles.modileprofileButton}>
                {cUserAvatar?.length ? <img src={cUserAvatar} /> : <ProfileIcon />}
                <p>
                  <span>
                    {cUserName.length > 17 ? `${cUserName.slice(0, 24)}...` : cUserName}
                  </span>
                </p>
              </div>
              <div className={styles.bars}>
                <button onClick={handleState}>
                  <img
                    className={bSideBarIsOpen ? styles.rotateToggleButton : ""}
                    src={RightArrow}
                    alt="Expandir/Recolher"
                  />
                </button>
              </div>
            </div>

            <div className={styles.topBar}>
              {menuItem.map((item, index) => (
                <SideBarItem key={index} item={item} bShowTexts={bShowTexts} />
              ))}
            </div>
            <div className={styles.middleBar}>
              {menuPersonal.map((item, index) => {
                if (!item?.bShow) return null
                return (
                  <RcmpLink
                    to={item.path}
                    key={index}
                    className={styles.link}
                    activeClassName={styles.activeLink}
                  >
                    <div className={styles.icon}>{item.icon}</div>
                    <div
                      className={bShowTexts ? `${styles.linkText}` : `${styles.hiddenText}`}
                    >
                      <span>{item.name}</span>
                    </div>
                  </RcmpLink>
                )
              })}
            </div>
            <ProfileButton bShowTexts={bShowTexts} />
            <div className={styles.graphicContainer}>
              <CircleGraphic nCurrent={nQuotaused} nTotal={nDiskSpace}>
                {nQuotaused}GB/{nDiskSpace}GB
              </CircleGraphic>
              {bShowTexts && (
                <div className={styles.texts}>
                  <p>
                    {intl.get("sidemenu.used-space", {
                      used: `${nQuotaused}`,
                      from: `${nDiskSpace}Gb`,
                    })}
                  </p>
                  <RcmpLink to="./modules">{intl.get("sidemenu.get-more-space")}</RcmpLink>
                </div>
              )}
            </div>

            <div className={styles.mobileGraphicContainer}>
              <div className={styles.storageProgressBar}>
                <div>
                  <div
                    className={styles.progress}
                    style={{ width: `${nStoragePercentage}%` }}
                  ></div>
                </div>
                <p>
                  {intl.get("sidemenu.used-space", {
                    used: `${nQuotaused}Gb`,
                    from: `${nDiskSpace}Gb`,
                  })}
                </p>
              </div>
              <Button variant="outline">{intl.get("sidemenu.get-more-space-short")}</Button>
            </div>
          </div>
        </div>
        <main id="mainContent" className={!bSideBarIsOpen ? styles.pageContent : styles.pageContentIsOpen}>
          {children}
        </main>
      </div>
    </div>
  )
}
